import { ref, onMounted, onUnmounted, watch, unref, isRef } from 'vue'
import { useStorePreCache } from '@/stores/storePreCache'
import { PRECACHE_CONFIG } from '@/utils/preCacheConfig'

/**
 * Helper to unwrap potentially reactive values
 */
const getValue = (val) => (isRef(val) ? val.value : val)

/**
 * Composable for registering component visibility with pre-cache system
 *
 * @param {Object} options
 * @param {string|number|Ref} options.id - Item ID (can be reactive)
 * @param {string} options.type - Item type ('review', 'title', 'list')
 * @param {number|Ref} [options.tmdbId] - TMDB ID (for titles)
 * @param {string|Ref} [options.tmdbType] - TMDB type (for titles)
 * @returns {Object} { elementRef, isVisible }
 */
export function usePreCache(options) {
  const elementRef = ref(null)
  const isVisible = ref(false)
  let observer = null
  let scrollHandler = null
  const storePreCache = useStorePreCache()

  const getItemData = () => {
    const id = getValue(options.id)
    if (!id) return null

    return {
      id,
      type: options.type,
      tmdbId: getValue(options.tmdbId),
      tmdbType: getValue(options.tmdbType)
    }
  }

  /**
   * Calculate distance from viewport center (0 = center, 1 = edge)
   */
  const calculateDistanceFromCenter = (entry) => {
    const rect = entry.boundingClientRect
    const viewportHeight = entry.rootBounds?.height || window.innerHeight
    const elementCenter = rect.top + rect.height / 2
    const viewportCenter = viewportHeight / 2

    // Normalize to 0-1 range
    const distance = Math.abs(elementCenter - viewportCenter) / viewportCenter
    return Math.min(distance, 1)
  }

  /**
   * Find the scroll container (ion-content or window)
   */
  const findScrollContainer = () => {
    if (!elementRef.value) return null

    // Look for ion-content parent (Ionic apps)
    let parent = elementRef.value.parentElement
    while (parent) {
      if (parent.tagName === 'ION-CONTENT') {
        return parent
      }
      parent = parent.parentElement
    }

    // Fall back to document
    return null
  }

  /**
   * Set up scroll activity tracking
   */
  const setupScrollTracking = () => {
    const scrollContainer = findScrollContainer()

    let scrollTimeout = null
    scrollHandler = () => {
      // Debounce scroll events
      if (scrollTimeout) return
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null
        storePreCache.recordActivity()
      }, PRECACHE_CONFIG.scrollDebounceMs)
    }

    if (scrollContainer) {
      scrollContainer.addEventListener('ionScroll', scrollHandler, {
        passive: true
      })
    }
    window.addEventListener('scroll', scrollHandler, { passive: true })
  }

  /**
   * Clean up scroll tracking
   */
  const cleanupScrollTracking = () => {
    if (scrollHandler) {
      const scrollContainer = findScrollContainer()
      if (scrollContainer) {
        scrollContainer.removeEventListener('ionScroll', scrollHandler)
      }
      window.removeEventListener('scroll', scrollHandler)
      scrollHandler = null
    }
  }

  /**
   * Set up IntersectionObserver
   */
  const setupObserver = () => {
    if (!PRECACHE_CONFIG.enabled) return
    if (!elementRef.value) return
    if (observer) return

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const item = getItemData()
          if (!item) return

          if (entry.isIntersecting) {
            isVisible.value = true
            storePreCache.registerVisible({
              ...item,
              distanceFromCenter: calculateDistanceFromCenter(entry)
            })
          } else {
            isVisible.value = false
            storePreCache.deregisterVisible(item)
          }
        })
      },
      {
        rootMargin: PRECACHE_CONFIG.rootMargin,
        threshold: PRECACHE_CONFIG.threshold
      }
    )

    observer.observe(elementRef.value)
  }

  /**
   * Clean up observer
   */
  const cleanupObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  // Watch for element ref changes
  watch(elementRef, (newEl) => {
    cleanupObserver()
    if (newEl) {
      setupObserver()
    }
  })

  // Watch for ID changes (in case props update)
  watch(
    () => getValue(options.id),
    (newId, oldId) => {
      if (oldId && isVisible.value) {
        storePreCache.deregisterVisible({
          id: oldId,
          type: options.type
        })
      }
    }
  )

  onMounted(() => {
    setupScrollTracking()
    if (elementRef.value) {
      setupObserver()
    }
  })

  onUnmounted(() => {
    cleanupObserver()
    cleanupScrollTracking()

    // Deregister if was visible
    const item = getItemData()
    if (item && isVisible.value) {
      storePreCache.deregisterVisible(item)
    }
  })

  return {
    elementRef,
    isVisible
  }
}
